import { action, atom } from '@reatom/framework'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { Result } from '@blade/std-lib'
import EN_DICTIONARY from '@root/shared/assets/localization/en.json'
import RU_DICTIONARY from '@root/shared/assets/localization/ru.json'
import { STORAGE_PREFIX } from '@root/shared/constants'

import { Lang } from '../model'
import { defaultLang } from '../model/Lang'

const STORAGE_KEY = `${STORAGE_PREFIX}/lang`

export type LanguageSelection = {
  type: 'manual' | 'system' | 'default'
  lang: Lang.Lang
}

export const languageSelectionAtom = atom<LanguageSelection>(
  { type: 'default', lang: Lang.defaultLang },
  'languageSelectionAtom',
)
export const systemLanguageAtom = atom<Lang.Lang | undefined>(
  undefined,
  'systemLanguageAtom',
)
export const languageAtom = atom((ctx) => {
  return ctx.spy(languageSelectionAtom).lang
}, 'languageAtom')

const EAGER_LANGS = new Set<Lang.Lang>(['en', 'ru'])

const loadedLangs = new Set<Lang.Lang>(['en', 'ru'])

const ensureLanguageBundleLoaded = async (lng: Lang.Lang): Promise<void> => {
  if (loadedLangs.has(lng) || EAGER_LANGS.has(lng)) {
    return
  }
  try {
    const mod = (await import(
      `@root/shared/assets/localization/${lng}.json`
    )) as { default: Record<string, string> }
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true)
    loadedLangs.add(lng)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`[i18n] failed to load ${lng}.json — falling back`, error)
  }
}

export const setLanguageAction = action(async (ctx, lng: LanguageSelection) => {
  if (lng.type === 'manual') {
    localStorage.setItem(STORAGE_KEY, lng.lang)
  } else {
    localStorage.removeItem(STORAGE_KEY)
  }
  await ctx.schedule(() => ensureLanguageBundleLoaded(lng.lang))
  await ctx.schedule(() => i18n.changeLanguage(lng.lang))
  await ctx.schedule(() => languageSelectionAtom(ctx, lng))
}, 'setLanguageAction')

/**
 * Sets the application's language preference and reloads the page.
 *
 * @param {Lang.Lang} lng - The language code to set as the current language.
 *
 * This function updates the language setting in local storage and then
 * reloads the page to apply the new language setting.
 */
window.__USE_LANGUAGE__ = (lng: Lang.Lang) => {
  localStorage.setItem(STORAGE_KEY, lng)
  location.reload()
}

const getInitialLanguage = (
  systemLang: Lang.Lang | undefined,
): LanguageSelection => {
  const persistedLang = Result.try({
    try: () => localStorage.getItem(STORAGE_KEY) ?? undefined,
    catch: () => undefined,
  }).pipe(Result.map(Lang.parse), Result.merge)

  if (persistedLang) {
    return {
      type: 'manual',
      lang: persistedLang,
    }
  }

  if (systemLang) {
    return {
      type: 'system',
      lang: systemLang,
    }
  }

  return {
    type: 'default',
    lang: Lang.defaultLang,
  }
}

export const initializeI18nAction = action(
  (ctx, rawSystemLngCode: string | undefined) => {
    const systemLang = Lang.parse(rawSystemLngCode)
    systemLanguageAtom(ctx, systemLang)
    const initLang = getInitialLanguage(systemLang)

    i18n.use(initReactI18next).init({
      resources: {
        en: {
          translation: EN_DICTIONARY,
        },
        ru: {
          translation: RU_DICTIONARY,
        },
      },
      lng: initLang.lang,
      fallbackLng: defaultLang,
      interpolation: {
        escapeValue: false,
      },
    })
    setLanguageAction(ctx, initLang)

    return i18n
  },
  'initializeI18nAction',
)

export type TransKey = keyof typeof EN_DICTIONARY
type RuTransKey = keyof typeof RU_DICTIONARY
type MissingKeys<T extends string> = Exclude<TransKey, T>
type MissingRuKeys = MissingKeys<RuTransKey>
type EnforceTrans<_T extends never> = 1
type _EnforceRuTrans = EnforceTrans<MissingRuKeys>

export const languageInfosAtom = atom((ctx) => {
  const currentLang = ctx.spy(languageAtom)

  const infos = [...Lang.SUPPORTED_LANGUAGES].map((lng): Lang.LangInfo => {
    const key = `language.${lng}` as never
    return {
      lang: lng,
      name: Lang.title(lng),
      localName: i18n.t(key, { lng: currentLang as string }),
    }
  })

  return infos
}, 'languageInfosAtom')

export const systemLanguageInfoAtom = atom((ctx) => {
  const systemLang = ctx.spy(systemLanguageAtom)
  const languageInfos = ctx.spy(languageInfosAtom)
  return languageInfos.find((i) => i.lang === systemLang)
}, 'systemLanguageInfoAtom')
